import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";

const POPULAR_TOPICS = [
  "Basetao Spreadsheet",
  "Basetao Review",
  "Basetao Shipping Cost",
  "Basetao Coupon",
  "Basetao Warehouse",
  "Is Basetao Legit?",
  "Basetao QC",
  "Basetao Sellers",
];

const VALUE_CARDS = [
  { icon: "ri-table-line", title: "value.card1.title", desc: "value.card1.desc" },
  { icon: "ri-team-line", title: "value.card2.title", desc: "value.card2.desc" },
  { icon: "ri-camera-line", title: "value.card3.title", desc: "value.card3.desc" },
  { icon: "ri-truck-line", title: "value.card4.title", desc: "value.card4.desc" },
  { icon: "ri-star-line", title: "value.card5.title", desc: "value.card5.desc" },
  { icon: "ri-shield-check-line", title: "value.card6.title", desc: "value.card6.desc" },
];

export default function HomeSections() {
  const { t } = useTranslation();

  return (
    <>
      {/* Popular topics */}
      <section className="px-4 md:px-6 py-8 border-b border-background-200/70 bg-background-50">
        <div className="mx-auto w-full max-w-7xl">
          <p className="text-xs md:text-sm font-semibold text-foreground-500 uppercase tracking-wide">
            {t("popular.title")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {POPULAR_TOPICS.map((topic) => (
              <span
                key={topic}
                className="rounded-full bg-background-100 border border-background-200 px-3.5 py-1.5 text-xs md:text-sm text-foreground-700 whitespace-nowrap"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-background-50">
        <div className="mx-auto w-full max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950">
            {t("about.h2")}
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">{t("about.p1")}</p>
          <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">{t("about.p2")}</p>
        </div>
      </section>

      {/* Value cards */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-background-100">
        <div className="mx-auto w-full max-w-7xl">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950">
              {t("value.h2")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{t("value.sub")}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {VALUE_CARDS.map((card) => (
              <div
                key={card.title}
                className="rounded-xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${card.icon} text-xl`}></i>
                </span>
                <h3 className="mt-5 text-base md:text-lg font-heading font-semibold text-foreground-950">
                  {t(card.title)}
                </h3>
                <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed">{t(card.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review + Shipping */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-background-50">
        <div className="mx-auto w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-background-200 bg-background-100 p-7 md:p-9 flex flex-col">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-shield-star-line text-xl"></i>
            </span>
            <h2 className="mt-5 text-xl md:text-2xl font-heading font-semibold text-foreground-950">
              {t("review.h2")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{t("review.copy")}</p>
            <Link
              to="/basetao-legit"
              className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
            >
              {t("review.cta")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-100 p-7 md:p-9 flex flex-col">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
              <i className="ri-ship-line text-xl"></i>
            </span>
            <h2 className="mt-5 text-xl md:text-2xl font-heading font-semibold text-foreground-950">
              {t("shipping.h2")}
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{t("shipping.copy")}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-secondary-600 transition-colors"
            >
              {t("shipping.cta")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-primary-50">
        <div className="mx-auto w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs md:text-sm font-medium text-primary-800">
              <i className="ri-shield-check-line"></i>
              {t("nav.safety")}
            </span>
            <h2 className="mt-4 text-2xl md:text-4xl font-heading font-semibold text-foreground-950">
              {t("safety.h2")}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{t("safety.cta")}</p>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li
                key={n}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <span className="text-sm md:text-base text-foreground-800">{t(`safety.item${n}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Article CTA */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-background-50">
        <div className="mx-auto w-full max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950">
            {t("articleSection.h2")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
            {t("articleSection.copy")}
          </p>
          <Link
            to="/basetao-legit"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm md:text-base font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            {t("button.readArticle")}
            <i className="ri-arrow-right-line text-base"></i>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 md:px-6 py-16 md:py-24 bg-primary-950">
        <div className="mx-auto w-full max-w-3xl text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-background-50">
            {t("cta.h2")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-background-200 leading-relaxed">{t("cta.copy")}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm md:text-base font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              {t("cta.primary")}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent-500 px-6 py-3 text-sm md:text-base font-semibold text-background-50 whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
            >
              {t("cta.secondary")}
              <i className="ri-shopping-bag-3-line text-base"></i>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}